import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeftIcon,
  QrCodeIcon,
  CalendarIcon,
  DocumentTextIcon
} from '@heroicons/react/24/outline';
import { FaDownload, FaCopy, FaPrint, FaShare } from 'react-icons/fa';
import { workshopAPI } from '../../services/api';
import { useToast } from '../Toast';
import { usePermissions } from '../../hooks/usePermissions';

const WorkshopAttendanceQR = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { checkPermission, Permission } = usePermissions();
  const [loading, setLoading] = useState(true);
  const [qrCodeData, setQrCodeData] = useState(null);
  const [workshop, setWorkshop] = useState(null);

  const fetchWorkshopAndQR = useCallback(async () => {
    try {
      setLoading(true);
      
      // Fetch workshop details
      const workshopResponse = await workshopAPI.getById(id);
      setWorkshop(workshopResponse.data);
      
      // Fetch attendance QR code
      const qrResponse = await workshopAPI.getAttendanceQR(id);
      setQrCodeData(qrResponse.data);
    } catch (error) {
      console.error('Error fetching workshop and QR code:', error);
      showToast('Failed to load workshop details', 'error');
      navigate(`/workshops/${id}`);
    } finally {
      setLoading(false);
    }
  }, [id, showToast, navigate]);

  useEffect(() => {
    fetchWorkshopAndQR();
  }, [fetchWorkshopAndQR]);

  // Check permissions after hooks
  if (!checkPermission(Permission.MANAGE_WORKSHOP_ATTENDANCE)) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="bg-gray-800 rounded-lg border border-gray-700 shadow-xl p-8 max-w-md mx-4 text-center">
          <div className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center mx-auto mb-4">
            <DocumentTextIcon className="h-8 w-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-red-400 mb-4">Access Denied</h1>
          <p className="text-gray-300 mb-6">
            You don't have permission to manage workshop attendance.
          </p>
          <button
            onClick={() => navigate(`/workshops/${id}`)}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
          >
            Back to Workshop
          </button>
        </div>
      </div>
    );
  }

  const downloadQRCode = () => {
    if (!qrCodeData?.qr_code_url) return;
    
    const link = document.createElement('a');
    link.href = qrCodeData.qr_code_url;
    link.download = `attendance-qr-${workshop?.name?.replace(/\s+/g, '-').toLowerCase()}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    showToast('QR code downloaded successfully', 'success');
  };

  const copyAttendanceUrl = () => {
    if (!qrCodeData?.attendance_url) return;
    
    navigator.clipboard.writeText(qrCodeData.attendance_url);
    showToast('Attendance URL copied to clipboard!', 'success');
  };

  const printQRCode = () => {
    if (!qrCodeData?.qr_code_url) return;
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Workshop Attendance QR Code</title>
          <style>
            body { font-family: Arial, sans-serif; text-align: center; padding: 20px; }
            .qr-container { margin: 20px auto; }
            .qr-code { max-width: 300px; }
            .info { margin: 20px 0; }
          </style>
        </head>
        <body>
          <h1>Workshop Attendance QR Code</h1>
          <div class="info">
            <p><strong>Workshop:</strong> ${workshop?.name || 'Unknown'}</p>
            <p><strong>Duration:</strong> ${workshop ? `${new Date(workshop.start_date).toLocaleDateString()} - ${new Date(workshop.end_date).toLocaleDateString()}` : 'Unknown'}</p>
          </div>
          <div class="qr-container">
            <img src="${qrCodeData.qr_code_url}" alt="QR Code" class="qr-code" />
          </div>
          <p>Scan this QR code to mark attendance</p>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  const shareQRCode = async () => {
    if (!qrCodeData?.attendance_url) return;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Workshop Attendance QR Code',
          text: 'Scan this QR code to mark your attendance',
          url: qrCodeData.attendance_url
        });
      } catch (error) {
        console.error('Error sharing:', error);
        copyAttendanceUrl();
      }
    } else {
      copyAttendanceUrl();
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate(`/workshops/${id}`)}
            className="p-2 text-gray-400 hover:text-white transition-colors"
          >
            <ArrowLeftIcon className="h-6 w-6" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-white">Workshop Attendance QR Code</h1>
            <p className="text-gray-400 mt-1">Permanent QR code for marking attendance</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* QR Code Display */}
        <div className="bg-gray-800 rounded-lg border border-gray-700 p-6">
          <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <QrCodeIcon className="h-5 w-5 text-blue-400" />
            Attendance QR Code
          </h2>
          
          <div className="space-y-4">
            {/* QR Code Image */}
            <div className="text-center">
              <div className="bg-gray-700 rounded-lg p-4 inline-block border border-gray-600">
                <img
                  src={qrCodeData?.qr_code_url}
                  alt="Attendance QR Code"
                  className="w-64 h-64 mx-auto"
                />
              </div>
            </div>
            
            {/* Workshop Info */}
            <div className="bg-gray-700 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <CalendarIcon className="h-4 w-4 text-blue-400" />
                <span className="text-sm font-medium text-gray-300">Workshop Details</span>
              </div>
              <div className="text-sm text-gray-400 space-y-1">
                <p><strong>Workshop:</strong> {workshop?.name}</p>
                <p><strong>Duration:</strong> {workshop ? `${new Date(workshop.start_date).toLocaleDateString()} - ${new Date(workshop.end_date).toLocaleDateString()}` : 'Unknown'}</p>
                <p><strong>Status:</strong> <span className="text-green-400">Permanent QR Code</span></p>
              </div>
            </div>
            
            {/* Action Buttons */}
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={downloadQRCode}
                className="bg-green-600 hover:bg-green-700 text-white p-3 rounded-lg flex flex-col items-center gap-2 transition-colors"
              >
                <FaDownload />
                <span className="text-sm">Download</span>
              </button>
              
              <button
                onClick={copyAttendanceUrl}
                className="bg-purple-600 hover:bg-purple-700 text-white p-3 rounded-lg flex flex-col items-center gap-2 transition-colors"
              >
                <FaCopy />
                <span className="text-sm">Copy URL</span>
              </button>
              
              <button
                onClick={printQRCode}
                className="bg-orange-600 hover:bg-orange-700 text-white p-3 rounded-lg flex flex-col items-center gap-2 transition-colors"
              >
                <FaPrint />
                <span className="text-sm">Print</span>
              </button>

              <button
                onClick={shareQRCode}
                className="bg-blue-600 hover:bg-blue-700 text-white p-3 rounded-lg flex flex-col items-center gap-2 transition-colors"
              >
                <FaShare />
                <span className="text-sm">Share</span>
              </button>
            </div>
          </div>
        </div>

        {/* Instructions */}
        <div className="bg-gray-800 rounded-lg border border-gray-700 p-6">
          <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <DocumentTextIcon className="h-5 w-5 text-green-400" />
            How to Use
          </h2>
          
          <div className="space-y-4">
            <div className="bg-blue-900 border border-blue-700 rounded-lg p-4">
              <h3 className="font-semibold text-blue-200 mb-2">For Students:</h3>
              <ul className="text-sm text-blue-100 space-y-1">
                <li>• Scan this QR code with your phone camera</li>
                <li>• Enter your registered phone number</li>
                <li>• Select today's date and current time</li>
                <li>• Submit to mark your attendance</li>
                <li>• You can only mark attendance once per day</li>
              </ul>
            </div>
            
            <div className="bg-green-900 border border-green-700 rounded-lg p-4">
              <h3 className="font-semibold text-green-200 mb-2">Features:</h3>
              <ul className="text-sm text-green-100 space-y-1">
                <li>• Permanent QR code - never expires</li>
                <li>• Automatic duplicate prevention</li>
                <li>• Date and time tracking</li>
                <li>• Phone number verification</li>
                <li>• Instant confirmation</li>
              </ul>
            </div>
            
            <div className="bg-gray-700 border border-gray-600 rounded-lg p-4">
              <h3 className="font-semibold text-gray-200 mb-2">Attendance URL:</h3>
              <p className="text-xs text-gray-400 break-all">
                {qrCodeData?.attendance_url}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WorkshopAttendanceQR; 